/**
 * Uploading a photo, a video or an HTML page: the file goes into the public `media` bucket
 * under the uploader's own folder, then submit_media() makes it a card. The server enforces
 * the account, the folder, the size and the type; this module refuses the obvious cases
 * before the bytes move.
 *
 * HTML is the path for AI-generated content: a page a model wrote, saved as a file (or pasted
 * as text). It is served back through our own viewer (public/cards/html/index.html) inside a
 * frame with an opaque origin and an inherited CSP, so it can draw and play but cannot reach
 * anything of ours or phone home.
 *
 * Since migration 0020 an upload lands on the bench, not live: the card is next for its maker at
 * once and joins everyone's feed after a person has opened it on a phone. The client sends a
 * SHA-256 of the bytes and the maker's own 'Made with' word through p_meta; the same file twice
 * is one card, and the word is theirs, never checked.
 */
import type { GameMeta } from '../lib/protocol';
import { ensureSession, supabase } from '../backend/supabase';
import { readBackAdded, type AddAnswer, type CardStatus } from './cards';

export const MAX_MEDIA_BYTES = 50 * 1024 * 1024;
/**
 * A page a model wrote is usually a few hundred KB; a whole HTML5 game build can be one file
 * of several MB (MONOLITH on itch.io is a single 5 MB index.html). The bucket allows 50.
 */
export const MAX_HTML_BYTES = 25 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const HTML_TYPES = ['text/html', 'application/xhtml+xml'];

export type MediaKind = 'image' | 'video' | 'html';

export function classifyMedia(file: { type: string; size: number; name: string }): { kind: MediaKind; ext: string } | { error: string } {
  const type = file.type.toLowerCase();
  // Browsers type a dragged .html as text/html, but a file saved from a chat can arrive with
  // no type at all, or as text/plain; the name settles it.
  const namedHtml = /\.html?$/i.test(file.name) && (type === '' || type === 'text/plain');
  const kind: MediaKind | null = IMAGE_TYPES.includes(type)
    ? 'image'
    : VIDEO_TYPES.includes(type)
      ? 'video'
      : HTML_TYPES.includes(type) || namedHtml
        ? 'html'
        : null;
  if (!kind) return { error: 'Photos (JPEG, PNG, WebP, GIF), videos (MP4, WebM, MOV) and HTML pages only.' };
  if (file.size === 0) return { error: 'That file is empty.' };
  if (kind === 'html' && file.size > MAX_HTML_BYTES) {
    return { error: `That page is ${(file.size / 1048576).toFixed(1)} MB; a page card is at most 25 MB — host big assets somewhere and link the address instead.` };
  }
  if (file.size > MAX_MEDIA_BYTES) return { error: `That is ${(file.size / 1048576).toFixed(0)} MB; the limit is 50 MB.` };
  const ext =
    kind === 'html'
      ? 'html'
      : ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'video/mp4': 'mp4', 'video/webm': 'webm', 'video/quicktime': 'mov' }[type] ?? 'bin');
  return { kind, ext };
}

/** Pasted HTML as a file, so the one upload path serves both. */
export function htmlFile(text: string, name = 'pasted.html'): File {
  return new File([text], name, { type: 'text/html' });
}

export type UploadResult =
  | { ok: true; meta: GameMeta; existing: boolean; status: CardStatus }
  | { ok: false; reason: string; code?: string };

/** 'Made with', as the server keeps it: trimmed, 40 characters at most, absent when empty. */
export const MADE_WITH_MAX = 40;

/** The lowercase hex SHA-256 of the bytes, or null where the browser offers no SubtleCrypto (an http page). */
export async function sha256Hex(blob: Blob): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  try {
    const digest = await subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/** submit_media's p_meta: the file's hash and the maker's word, each only when there is one. */
export function mediaMeta(contentHash: string | null, madeWith?: string): { content_hash?: string; made_with?: string } {
  const m: { content_hash?: string; made_with?: string } = {};
  if (contentHash) m.content_hash = contentHash;
  const w = (madeWith ?? '').trim().slice(0, MADE_WITH_MAX);
  if (w) m.made_with = w;
  return m;
}

export async function uploadMedia(file: File, title?: string, madeWith?: string): Promise<UploadResult> {
  const c = classifyMedia(file);
  if ('error' in c) return { ok: false, reason: c.error };
  if (!supabase) return { ok: false, reason: 'This build has no backend, so uploads cannot be saved.' };
  const session = await ensureSession();
  if (!session) return { ok: false, reason: 'Could not start a session — try again in a moment.' };
  const user = session.user as { id: string; is_anonymous?: boolean };
  if (user.is_anonymous) return { ok: false, reason: 'Create an account to upload.', code: 'account_required' };

  const hash = await sha256Hex(file);
  const name = `${crypto.randomUUID()}.${c.ext}`;
  const path = `${user.id}/${name}`;
  const contentType = c.kind === 'html' ? 'text/html' : file.type;
  const up = await supabase.storage.from('media').upload(path, file, { contentType, upsert: false, cacheControl: '31536000' });
  if (up.error) return { ok: false, reason: friendlyStorageError(up.error.message) };

  const { data, error } = await supabase.rpc('submit_media', {
    p_kind: c.kind,
    p_path: path,
    p_title: title ?? null,
    p_meta: mediaMeta(hash, madeWith),
  });
  if (error) {
    if (error.code === '53400') return { ok: false, reason: 'That is a lot of uploads for one hour. Try again later.' };
    return { ok: false, reason: error.message || 'The server refused the upload.' };
  }
  const r = (data ?? {}) as AddAnswer;
  if (!r.ok || !r.card_id) return { ok: false, reason: r.reason ?? 'Could not make a card of that.', code: r.code };
  if (r.existing) {
    // The same bytes were already a card: the file just stored is not one, so it goes again.
    try {
      await supabase.storage.from('media').remove([path]);
    } catch {
      /* a stray file in the maker's own folder is harmless */
    }
  }
  return readBackAdded(r, 'file');
}

function friendlyStorageError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('mime') || m.includes('type')) return 'That file type is not allowed.';
  if (m.includes('size') || m.includes('too large') || m.includes('exceed')) return 'That file is over the 50 MB limit.';
  if (m.includes('row-level security') || m.includes('policy') || m.includes('unauthorized')) return 'Create an account with a handle to upload.';
  return message || 'The upload failed.';
}
